import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Alert, FormGroup, Input, Label } from 'reactstrap';
import { TypedMemo } from '../common/types';
import { teamErrorMessage } from '../team/teamErrorMessage';
import { useWorkspaces } from './useWorkspaces';
import { useMembersSeeEarnings } from './useMembersSeeEarnings';
import { membersSeeEarningsMessages as messages } from './membersSeeEarningsMessages';
import { Panel, PanelBody, PanelHead } from '../music24/components/primitives/Panel';
import { withBrandWord } from '../music24/components/primitives/BrandWord';

/**
 * design: your-intelligence e4. "What members can see": the owner decides if the members of the
 * workspace see the earnings and the Ditto balance. Off by default. The server enforces it: a
 * member gets no money field while it is off. Only the owner sees this panel.
 */
const MembersSeeEarningsPanelComponent: React.FC = () => {
  const intl = useIntl();
  const { active } = useWorkspaces();
  const { setEnabled, isPending, error } = useMembersSeeEarnings();
  const isOn = active?.membersSeeEarnings === true;
  const workspace = active?.name ?? '';

  const toggle = useCallback(() => {
    setEnabled(!isOn).catch(() => undefined);
  }, [isOn, setEnabled]);

  if (active === null) {
    return null;
  }

  return (
    <Panel as="section" className="mb-4" data-testid="members-see-earnings">
      <PanelHead title={intl.formatMessage(messages.title)} />
      <PanelBody>
        <FormGroup switch className="mb-2">
          <Input
            id="members-see-earnings"
            type="switch"
            role="switch"
            checked={isOn}
            disabled={isPending}
            onChange={toggle}
          />
          <Label for="members-see-earnings" check>
            {intl.formatMessage(messages.label)}
          </Label>
        </FormGroup>
        <p className="text-muted small mb-0">
          {isOn
            ? withBrandWord(intl.formatMessage(messages.onText, { workspace }))
            : intl.formatMessage(messages.offText)}
        </p>
        {error !== null && error !== undefined && (
          <Alert color="danger" className="mt-3 mb-0">
            {teamErrorMessage(intl, error)}
          </Alert>
        )}
      </PanelBody>
    </Panel>
  );
};

export const MembersSeeEarningsPanel = TypedMemo(MembersSeeEarningsPanelComponent);
